import { Link } from "react-router-dom";
import {
  BookOpen, ClipboardList, CheckCircle2, AlertTriangle, ShieldCheck,
  Sparkles, ArrowRight, Info, FileText, Users, Target, Activity,
  Calendar as CalendarIcon, Utensils, Pill, Phone, RefreshCw,
  Heart, ChevronRight, Mic, GitBranch,
} from "lucide-react";

/**
 * /help/care-plan-guide — Guião "Como construir um bom Plano de Cuidados".
 *
 * Baseado em:
 *   • estrutura de blocos declarada em ``care_plan_module/models.py``
 *     (CarePlanBlocks + BLOCK_KEYS — as 28 chaves editáveis)
 *   • "Manual do Cuidador" carregado em /app/backend/knowledge/ (350 secções)
 *     e servido via ``manual_rag_service`` como RAG para as respostas AI
 *   • fluxo de aprovações TSC / DT / Gestor (models.CarePlanApprovalsBlock)
 *
 * Página estática — zero calls à API. Renderiza um índice sticky à esquerda
 * e o guião prolongado à direita. Adere ao design system Linear/Vercel.
 */

const NAV = [
  { id: "porque",          title: "Porquê um Plano de Cuidados",       icon: BookOpen },
  { id: "estrutura",       title: "1 · Estrutura em 3 vistas",         icon: FileText },
  { id: "fluxo",           title: "2 · Fluxo de construção",           icon: GitBranch },
  { id: "vista-interna",   title: "3 · Vista Interna (28 blocos)",     icon: ClipboardList },
  { id: "vista-af",        title: "4 · Vista Ajudante Familiar",       icon: Heart },
  { id: "vista-cliente",   title: "5 · Vista Cliente / Família",       icon: Users },
  { id: "ia",              title: "6 · Usar a IA correctamente",       icon: Sparkles },
  { id: "aprovacoes",      title: "7 · Aprovações TSC / DT / Gestor",  icon: ShieldCheck },
  { id: "regras",          title: "8 · Regras críticas (Caring)",      icon: AlertTriangle },
  { id: "checklist",       title: "9 · Checklist final",               icon: CheckCircle2 },
];


// ── Building blocks ─────────────────────────────────────────────────────

function Kbd({ children }) {
  return (
    <code className="inline-flex items-center px-1.5 py-0.5 rounded border border-border bg-muted/40 text-[11.5px] font-mono text-foreground">
      {children}
    </code>
  );
}

function Section({ id, icon: Icon, kicker, title, children }) {
  return (
    <section id={id} data-testid={`cp-section-${id}`}
             className="bg-card border border-border rounded-xl p-6 lg:p-8 scroll-mt-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-lg bg-muted/40 border border-border flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-foreground" strokeWidth={1.6} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold font-heading">
            {kicker}
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-foreground leading-tight">
            {title}
          </h2>
        </div>
      </div>
      <div className="space-y-4 text-sm leading-relaxed text-foreground font-body">
        {children}
      </div>
    </section>
  );
}

function BadGood({ bad, good }) {
  return (
    <div className="grid md:grid-cols-2 gap-3 mt-3">
      <div className="rounded-lg border border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger-bg))]/40 p-4">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--danger))] mb-1.5">✗ Fraco</div>
        <div className="text-foreground text-[13px] italic">"{bad}"</div>
      </div>
      <div className="rounded-lg border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/40 p-4">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--success))] mb-1.5">✓ Bom</div>
        <div className="text-foreground text-[13px] italic">"{good}"</div>
      </div>
    </div>
  );
}

function Callout({ kind = "info", title, children }) {
  const styles = {
    info: "border-l-blue-500 bg-[hsl(var(--info-bg))]/40",
    warn: "border-l-amber-500 bg-[hsl(var(--warning-bg))]/40",
    danger: "border-l-red-500 bg-[hsl(var(--danger-bg))]/40",
    tip: "border-l-emerald-500 bg-[hsl(var(--success-bg))]/40",
  };
  const iconMap = { info: Info, warn: AlertTriangle, danger: AlertTriangle, tip: Sparkles };
  const I = iconMap[kind];
  return (
    <div className={`border border-border border-l-4 ${styles[kind]} rounded-lg p-4 flex gap-3`}>
      <I className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" strokeWidth={1.6} />
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-foreground mb-1">{title}</div>
        <div className="text-[13px] text-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Block({ nome, chave, quando, comoPreencher, exemplo }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <h4 className="font-heading text-sm text-foreground tracking-tight">{nome}</h4>
        <Kbd>{chave}</Kbd>
      </div>
      <div className="text-[12px] text-muted-foreground space-y-1.5">
        <div><b className="text-foreground">Quando preencher:</b> {quando}</div>
        <div><b className="text-foreground">Como:</b> {comoPreencher}</div>
        {exemplo && (
          <div className="mt-2 pt-2 border-t border-border/60 text-[12px] text-muted-foreground italic">
            <b className="not-italic text-foreground">Exemplo:</b> {exemplo}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Page ────────────────────────────────────────────────────────────────

export default function CarePlanGuide() {
  return (
    <div className="min-h-screen bg-muted/40" data-testid="cp-guide-root">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">

        {/* Header */}
        <header className="mb-10 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-heading font-semibold mb-2">
              Guião prático · Gestores de Cliente + TSC + DT
            </div>
            <h1 className="font-heading text-3xl lg:text-4xl tracking-tight text-foreground leading-tight">
              Como construir um bom Plano de Cuidados
            </h1>
            <p className="text-[15px] text-muted-foreground mt-3 max-w-3xl leading-relaxed">
              Baseado na estrutura de 28 blocos do sistema, no <b>Manual do Cuidador</b> da Universidade
              de Coimbra (349 secções carregadas na knowledge base) e no fluxo de aprovações TSC / DT /
              Gestor. O guião começa <b>antes da RT</b> (convencer o cliente a aceitá-la) e termina no
              congelamento da versão — um plano completo demora entre 45 min e 2h a partir da gravação.
            </p>
          </div>
          <Link
            to="/help"
            data-testid="cp-back-to-manual"
            className="inline-flex items-center gap-2 h-9 px-3 bg-card border border-border rounded-md text-[13px] text-foreground hover:bg-muted transition-colors font-heading"
          >
            ← Manual do utilizador
          </Link>
        </header>

        <div className="grid lg:grid-cols-[240px,1fr] gap-8">

          {/* Nav sidebar (sticky) */}
          <aside className="lg:sticky lg:top-8 h-fit space-y-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-heading font-semibold px-3 mb-2">
              Índice
            </div>
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-testid={`cp-nav-${item.id}`}
                className="flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] text-muted-foreground hover:bg-card hover:text-foreground hover:border hover:border-border border border-transparent transition-all font-heading"
              >
                <item.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
                <span className="truncate">{item.title}</span>
              </a>
            ))}
          </aside>

          {/* Content */}
          <main className="space-y-6">

            {/* 0 — Porquê */}
            <Section id="porque" icon={BookOpen} kicker="Ponto de partida" title="Porquê um Plano de Cuidados">
              <p>
                O Plano de Cuidados (PC) é o <b>documento operacional único</b> que traduz o que o
                cliente disse na RT em acções concretas para a Ajudante Familiar, o que a família
                pode esperar da Caring, e o que a equipa técnica interna deve monitorizar. Sem PC bem
                feito, cada visita é reinvento e a equipa perde continuidade.
              </p>
              <Callout kind="info" title="A regra dos 3 públicos">
                O mesmo plano é lido por <b>3 audiências diferentes</b> — TSC / DT / gestores (vista interna),
                Ajudantes Familiares (vista AF) e Cliente / Família (vista pública). O sistema já filtra
                automaticamente o que cada uma vê, mas <b>é o autor</b> que decide em que bloco escrever cada coisa.
                Escreve no sítio errado e a Ajudante nunca vai saber que a D. Maria não pode tomar café à noite.
              </Callout>
              <p>
                As <Kbd>28 chaves de bloco</Kbd> declaradas em <Kbd>CarePlanBlocks</Kbd> não são
                arbitrárias — cada uma corresponde a uma secção específica de uma das 3 vistas exportadas
                (<Kbd>template_internal.docx</Kbd>, <Kbd>template_caregiver.docx</Kbd>, <Kbd>template_client.docx</Kbd>
                em <Kbd>care_plan_module/templates/</Kbd>). Ver na secção 3 abaixo.
              </p>
            </Section>

            {/* 1 — Estrutura */}
            <Section id="estrutura" icon={FileText} kicker="Passo 1" title="Estrutura em 3 vistas">
              <p>
                Cada Plano de Cuidados é exportado em 3 documentos .docx distintos, gerados a partir
                dos mesmos <Kbd>blocks</Kbd> mas com filtros de visibilidade diferentes
                (<Kbd>care_plan_module/visibility.py</Kbd>):
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Vista</div>
                  <h4 className="font-heading text-foreground mb-2">Interna</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Para o TSC, DT e Gestor. Inclui perfil da pessoa, avaliação funcional, objectivos,
                    medicação, riscos, plano semanal, comunicação-escalação, follow-ups, notas
                    confidenciais. <b>Nunca partilhar com o cliente.</b>
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Vista</div>
                  <h4 className="font-heading text-foreground mb-2">Ajudante Familiar</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Para a AF ler antes e durante cada visita. Sem jargão técnico. Tarefas diárias,
                    como conversar, o que fazer antes de sair, quando ligar ao Gestor. É o único
                    documento que a AF vê.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Vista</div>
                  <h4 className="font-heading text-foreground mb-2">Cliente / Família</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Publicada em <Kbd>/care-plan/&lt;token&gt;</Kbd>. Preferências, equipa, atenções
                    especiais e etapas de acompanhamento. Tom mais leve e humano — mas sem prometer
                    resultados médicos.
                  </p>
                </div>
              </div>
              <Callout kind="warn" title="A escolha do bloco importa">
                Um risco de queda escrito em <Kbd>family_notes</Kbd> vai parar ao doc do cliente.
                O mesmo risco em <Kbd>risks</Kbd> ou <Kbd>internal_notes</Kbd> fica interno. Se
                tiveres dúvida, coloca sempre em <Kbd>internal_notes</Kbd> primeiro — é reversível.
              </Callout>
            </Section>

            {/* 2 — Fluxo */}
            <Section id="fluxo" icon={GitBranch} kicker="Passo 2" title="Fluxo canónico de construção">
              <Callout kind="info" title="Antes de tudo: a RT tem de estar agendada">
                Nenhum destes passos é possível se o cliente não aceitar a <b>Reunião Técnica</b>.
                Se a RT ainda <b>não está agendada</b>, o teu primeiro trabalho é convencer o
                cliente a aceitá-la (Passo 1 abaixo). Só depois é que o fluxo do Plano de Cuidados
                começa de facto — a partir do Passo 2.
              </Callout>
              <ol className="space-y-3 list-none">
                {[
                  { n: "1", t: "Convencer o cliente a aceitar a Reunião Técnica (RT).",
                    d: (
                      <>
                        Só aplicável quando a RT ainda <b>não está agendada</b>. Sem RT não há
                        transcrição, não há análise IA, não há Plano de Cuidados. Argumentos
                        Caring a usar:
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li><b>Avaliação técnica sem custo</b> — a RT é gratuita e é o único
                              momento em que uma técnica da Caring vai a casa perceber, ao vivo,
                              o contexto real da pessoa cuidada.</li>
                          <li><b>Dignidade e segurança</b> — sem RT arriscamo-nos a montar um
                              serviço que ignora rotinas, riscos e preferências. A RT existe
                              para <i>ninguém</i> receber cuidado genérico.</li>
                          <li><b>Plano à medida</b> — a proposta final e o Plano de Cuidados
                              nascem <b>desta</b> conversa. Sem ela, só se consegue um orçamento
                              padrão — nunca um plano.</li>
                          <li><b>Compromisso zero</b> — a RT não obriga o cliente a contratar.
                              É um espaço de escuta e recomendação.</li>
                        </ul>
                        <span className="block mt-2 text-muted-foreground text-[12px]">
                          Após aceite, agenda no Outlook/Teams (Manual · Passo 6). Assim que
                          entra em <Kbd>qt_agendada</Kbd>, avança para o passo seguinte.
                        </span>
                      </>
                    ) },
                  { n: "2", t: "Gravar a RT no Teams (obrigatório).",
                    d: "Sem gravação → sem transcript → sem pré-preenchimento IA. Perde-se 60% da eficiência. Ver Manual do Utilizador · Passo 2." },
                  { n: "3", t: "Ler a análise IA no painel Análise da Reunião Técnica.",
                    d: <>Chega sozinha em ≤5 min via <Kbd>rt_graph_polling</Kbd>. Whisper transcreve, Claude estrutura em objectivos, necessidades, riscos e acções.</> },
                  { n: "4", t: "Aplicar sugestões AI aos blocos com confidence ≥ 70%.",
                    d: <>Clica <b>"Ver análise"</b> → selecciona os campos → <b>"Aplicar ao Plano"</b>. Confidence &lt; 70% aparece com badge amarelo — revê primeiro.</> },
                  { n: "5", t: "Preencher manualmente os blocos que a IA não cobre.",
                    d: "Nomeadamente: preferências, contactos, follow-up plan, notas internas. Ver secção 3 abaixo para cada bloco." },
                  { n: "6", t: "Gerar as 3 vistas em pré-visualização.",
                    d: <>Botão <b>"Pré-visualizar .docx"</b> na página <Kbd>/care-plans</Kbd> — abre os 3 docs sem congelar. Reler tudo como se fosses o leitor.</> },
                  { n: "7", t: "Pedir aprovações TSC → DT → Gestor.",
                    d: "Os 3 aprovam. Antes de todos aprovarem, a versão fica em rascunho e não é vista pelo cliente." },
                  { n: "8", t: "Congelar a versão (\"Freeze V1\").",
                    d: "Só após congelamento é que o link público /care-plan/<token> passa a mostrar \"Documento validado\". Edições depois criam V2 automaticamente." },
                ].map((step) => (
                  <li key={step.n} data-testid={`cp-flow-step-${step.n}`} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-md bg-foreground text-background flex items-center justify-center font-heading text-[13px] shrink-0">
                      {step.n}
                    </div>
                    <div className="flex-1">
                      <div className="font-heading text-foreground text-[14px]">{step.t}</div>
                      <div className="text-[13px] text-muted-foreground mt-0.5 leading-relaxed">{step.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>

            {/* 3 — Vista Interna */}
            <Section id="vista-interna" icon={ClipboardList} kicker="Passo 3" title="Vista Interna — como preencher cada bloco">
              <p>
                Estes blocos formam o núcleo clínico do plano. A ordem aqui é a mesma ordem em que
                aparecem no docx interno. Preencher todos, mesmo que curtos.
              </p>

              <h3 className="font-heading text-foreground text-base mt-6 mb-3">Identificação & Contexto</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Identificação do serviço" chave="service_identification"
                       quando="Sempre — auto-preenchido a partir do lead."
                       comoPreencher="Confirma dados demográficos: nome, idade, endereço, doenças crónicas relevantes, contactos de emergência."
                       exemplo="D. Maria Silva, 84 anos, viúva, Alzheimer em fase moderada, tem filha em Lisboa (número operacional)." />
                <Block nome="Resumo crítico ('LER ANTES')" chave="critical_summary"
                       quando="Antes de qualquer AF visitar."
                       comoPreencher="Em 3-5 linhas: o que a AF PRECISA de saber nos primeiros 60 segundos. Riscos imediatos, alergias, tabus (por exemplo, evitar temas)."
                       exemplo="Diabética insulino-dependente. Não pode comer açúcar. Nunca falar do marido falecido (chora). Se cair, chamar 112 antes de tudo." />
              </div>

              <h3 className="font-heading text-foreground text-base mt-6 mb-3">A Pessoa</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Perfil da pessoa" chave="person_profile"
                       quando="Ao construir a versão inicial."
                       comoPreencher="Lista de traços: profissão anterior, gostos, medos, temas favoritos, o que a torna feliz. NÃO é o clínico."
                       exemplo="Ex-professora primária. Adora música clássica. Detesta televisão à noite. Fica ansiosa se lhe mexem na mala." />
                <Block nome="Avaliação funcional" chave="functional_assessment"
                       quando="Após RT com a família."
                       comoPreencher="Nível de autonomia em cada actividade (higiene, alimentação, mobilidade, cognição). Usa escala 1-5 ou 'autónomo / parcial / dependente'."
                       exemplo="Higiene: parcial (precisa de ajuda no banho). Mobilidade: autónomo com bengala. Cognição: perde-se em conversas longas." />
              </div>
              <BadGood
                bad="Muito debilitada, precisa muita ajuda."
                good="Alimentação: autónoma na mesa se comida cortada. Higiene: precisa de ajuda a entrar/sair do chuveiro. Cognição: reconhece cuidadores há +2 meses; confunde nomes de familiares após 15h."
              />

              <h3 className="font-heading text-foreground text-base mt-6 mb-3">Plano de Cuidados propriamente dito</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Necessidades & Objectivos" chave="needs_objectives"
                       quando="Sempre — é o coração do plano."
                       comoPreencher="Para cada necessidade identificada na RT, um objectivo mensurável a 3 meses. Formato: 'Necessidade → Objectivo → Indicador de sucesso'."
                       exemplo="Necessidade: mobilidade reduzida → Objectivo: manter marcha com bengala → Indicador: caminhar 15 min/dia sem sinais de cansaço." />
                <Block nome="Plano semanal" chave="weekly_plan"
                       quando="Após definir objectivos."
                       comoPreencher="Grelha por dia da semana + período (manhã/tarde/noite). Cada célula tem 1-3 acções concretas. É a base do horário da AF."
                       exemplo="Segunda manhã: higiene + banho assistido + pequeno-almoço + caminhada até jardim (15 min)." />
                <Block nome="Nutrição & Hidratação" chave="nutrition_hydration"
                       quando="Se houver restrições ou dieta específica."
                       comoPreencher="Restrições absolutas, preferências, líquidos/dia, texturas se disfagia. Nunca prescrever suplementos sem indicação médica."
                       exemplo="Diabética, dieta hipoglicémica prescrita pela nutricionista. 1.5L água/dia. Não engolir bem carne, cortar sempre." />
                <Block nome="Medicação (nível clínico)" chave="medication_level"
                       quando="Se toma medicação regular."
                       comoPreencher="Só nomes genéricos + horário + observações. NUNCA doses (isso é competência médica). Alergias em bold."
                       exemplo="Metformina — manhã e noite após refeição. Alergia PENICILINA. Ver caixa semanal preparada pela filha ao sábado." />
              </div>
              <Callout kind="danger" title="Nunca no plano">
                Doses concretas, alterações de medicação, diagnósticos novos. A Caring não prescreve.
                Se a AF nota efeito adverso, escala ao Gestor que contacta o médico de família.
              </Callout>

              <h3 className="font-heading text-foreground text-base mt-6 mb-3">Riscos, comunicação e follow-up</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Riscos" chave="risks"
                       quando="Sempre. Sem excepção."
                       comoPreencher="Cada risco: descrição + probabilidade (baixa/média/alta) + acção preventiva + resposta se acontecer."
                       exemplo="Risco de queda no chuveiro (alta): tapete anti-derrapante instalado + AF entra sempre antes. Se cair: verificar consciência → 112 → filha." />
                <Block nome="Comunicação & escalação" chave="communication_escalation"
                       quando="Sempre."
                       comoPreencher="Quem chama a quem, quando, por que canal. Formato: 'Situação → Contactar X (telefone Y) → Timing'."
                       exemplo="Febre >38.5°C → Filha Ana (9XX XXX XXX) imediato + Gestor no chat ClickUp em 30 min. Recusa comer 2 refeições consecutivas → só Gestor." />
                <Block nome="Plano de follow-up" chave="followup_plan"
                       quando="Sempre."
                       comoPreencher="Ritmo de revisão: quando o Gestor liga à família, quando o TSC visita, quando se refaz o plano. Semanal / quinzenal / mensal."
                       exemplo="Semana 1-4: Gestor liga sexta 16h. TSC visita ao 15º dia. Reavaliação do plano ao 90º dia." />
                <Block nome="Contactos operacionais" chave="contacts"
                       quando="Sempre."
                       comoPreencher="Médico de família, farmácia, familiar-chave, enfermeiro se houver. Rótulo + nome + telefone + notas ('só chamar antes das 20h')."
                       exemplo="Dr. Rui Alves (Médico Fam.) — 217 XXX XXX — só terças e quintas manhã." />
              </div>

              <h3 className="font-heading text-foreground text-base mt-6 mb-3">Notas confidenciais</h3>
              <Block nome="Notas internas" chave="internal_notes"
                     quando="Para tudo o que só a Caring vê."
                     comoPreencher="Situações familiares delicadas, histórico de conflito, observações que ajudam o Gestor mas nunca deveriam chegar ao cliente."
                     exemplo="Filha e filho discutem sobre quem paga o serviço — endereçar comunicações sempre à filha (contacto pagador). Cliente esconde declínio cognitivo à família." />
            </Section>

            {/* 4 — Vista AF */}
            <Section id="vista-af" icon={Heart} kicker="Passo 4" title="Vista Ajudante Familiar — o documento operacional">
              <p>
                O <Kbd>template_caregiver.docx</Kbd> é lido pela AF antes de cada turno. Escreve
                como se estivesses a falar com alguém que <b>ainda não conhece a pessoa</b>. Zero
                jargão. Frases curtas.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Atenção antes de começar" chave="caregiver_before_start"
                       quando="Sempre. É a primeira secção que a AF lê."
                       comoPreencher="5-8 linhas com o essencial: como tratar a pessoa, alergias, tabus, o que fazer nos primeiros 15 minutos."
                       exemplo="Tratar por D. Maria (nunca 'senhora'). Não falar de política nem de morte. Ao chegar, sentar-se ao lado dela na sala 5 min antes de qualquer tarefa." />
                <Block nome="Tratar por" chave="caregiver_tratar_por"
                       quando="Sempre."
                       comoPreencher="Como o próprio se identifica. Curto — 3-5 palavras."
                       exemplo="D. Maria" />
                <Block nome="Descrição da pessoa (livre)" chave="caregiver_person_desc"
                       quando="Sempre."
                       comoPreencher="Um parágrafo narrativo, humano. Origem, personalidade, temas seguros de conversa."
                       exemplo="A D. Maria foi professora primária durante 40 anos em Coimbra. Gosta de música clássica e de recordar aulas. Falar de flores é sempre bem-vindo, ela cultivou-as toda a vida." />
                <Block nome="Tarefas diárias" chave="caregiver_daily_tasks"
                       quando="Sempre."
                       comoPreencher="Grelha estruturada: acção + horário + como fazer. É o roteiro passo-a-passo da AF."
                       exemplo="09:00 · Preparar pequeno-almoço (cha com leite + torrada) · Servir na sala, não na cozinha (ela prefere)." />
                <Block nome="Como prestar cuidados" chave="care_delivery"
                       quando="Se houver técnica específica."
                       comoPreencher="Como fazer a higiene, transferências, alimentação assistida. Passos concretos e imagens mentais."
                       exemplo="Banho: colocar cadeira de banho, encher chuveiro, chamá-la, esperar 30s antes de retirar roupa (dá-lhe controlo)." />
                <Block nome="Antes de terminar o turno" chave="caregiver_before_ending"
                       quando="Sempre."
                       comoPreencher="Checklist de 4-6 itens que a AF cumpre nos últimos 15 min: registar tarefas, arrumar, deixar bilhete, telefonar ao Gestor se algo relevante."
                       exemplo="1. Preencher app · 2. Deixar copo de água ao lado da cama · 3. Ligar luz de presença · 4. Enviar mensagem WhatsApp ao Gestor com o resumo do dia." />
                <Block nome="Quando contactar o Gestor" chave="caregiver_when_to_contact"
                       quando="Sempre."
                       comoPreencher="Lista clara de gatilhos. A AF não deve ficar em dúvida sobre se pode/deve ligar."
                       exemplo="Ligar imediatamente se: febre >38, queda, recusa comer 2 refeições, choro persistente, familiar aparece agitado." />
              </div>
            </Section>

            {/* 5 — Vista Cliente */}
            <Section id="vista-cliente" icon={Users} kicker="Passo 5" title="Vista Cliente / Família — o documento público">
              <p>
                Publicado em <Kbd>/care-plan/&lt;token&gt;</Kbd> quando a versão for congelada e o
                banner "PROVISÓRIO" for retirado. Tom humano, sem prometer resultados médicos.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <Block nome="Introdução" chave="client_summary_intro"
                       quando="Sempre."
                       comoPreencher="2-3 parágrafos que o cliente lê primeiro. Foco em segurança, dignidade e continuidade — não em diagnóstico."
                       exemplo="A Caring propõe acompanhar a D. Maria com um plano centrado na sua dignidade e conforto. Priorizamos manter a sua rotina, respeitar as suas preferências e garantir uma equipa estável." />
                <Block nome="Preferências (chave-valor)" chave="client_preferences_kv"
                       quando="Sempre."
                       comoPreencher="Preferências relevantes: refeições, banho, temas, música, horários. O que a família confirma que a Caring vai respeitar."
                       exemplo="Refeições: sopa sempre ao almoço · Banho: manhã · Música: fados e Amália · Sesta: 14h-15h." />
                <Block nome="Atenções especiais" chave="client_special_attention"
                       quando="Se houver algo além do standard."
                       comoPreencher="Item + porquê + como a Caring vai gerir. É o compromisso público."
                       exemplo="Alergia à penicilina — todos os elementos da equipa foram informados; caixa de medicação tem etiqueta vermelha." />
                <Block nome="Equipa" chave="client_team_members"
                       quando="Após atribuição da equipa."
                       comoPreencher="Nome, função, foto (opcional). Só quem realmente vai ao domicílio. Se muda, gerar nova versão."
                       exemplo="Ana (AF principal), Beatriz (backup), Gestora Daniela." />
                <Block nome="Etapas de acompanhamento" chave="client_followup_stages"
                       quando="Sempre."
                       comoPreencher="Timeline pública: quando o Gestor contacta, quando o TSC visita, quando o plano é reavaliado. Dá previsibilidade."
                       exemplo="Dia 15: visita do TSC. Dia 30: chamada da Gestora à família. Dia 90: reavaliação do plano." />
              </div>
              <Callout kind="warn" title="Não escrever aqui">
                • Doenças e diagnósticos específicos<br/>
                • Cognição, declínio ou perdas<br/>
                • Riscos clínicos (esses vão em <Kbd>risks</Kbd> na vista interna)<br/>
                • Conflitos familiares<br/>
                • Custos, condições de pagamento, seguros<br/>
              </Callout>
            </Section>

            {/* 6 — IA */}
            <Section id="ia" icon={Sparkles} kicker="Passo 6" title="Usar a IA correctamente">
              <p>
                A Caring tem <b>duas peças de IA</b> a apoiar o Plano de Cuidados. Compreendê-las
                evita retrabalho.
              </p>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-border bg-card p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Mic className="w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
                    <h4 className="font-heading text-foreground">Análise da RT (Whisper + Claude)</h4>
                  </div>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">
                    Transcreve o áudio da reunião e estrutura em objectivos, necessidades, riscos e
                    acções. Cada campo vem com <b>confidence 0-1</b>. Se confidence ≥ 0.7, aparece a
                    verde e podes aplicar directo. Se &lt; 0.7 (badge amarelo), lê o transcript e
                    corrige antes de aplicar.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
                    <h4 className="font-heading text-foreground">Assistente Manual do Cuidador (RAG)</h4>
                  </div>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">
                    O <b>Manual do Cuidador da Universidade de Coimbra</b> está indexado (349 secções).
                    Botão <b>Assistente Caring</b> no canto do lead — pergunta em linguagem natural
                    ("como abordar higiene em paciente com Alzheimer moderado?"), a IA responde
                    citando as secções do manual relevantes.
                  </p>
                </div>
              </div>

              <Callout kind="tip" title="Boas perguntas ao Assistente">
                • "Como planear a transferência para uma pessoa com hemiplegia?"<br/>
                • "Quais são os sinais de alarme numa pessoa acamada?"<br/>
                • "Que rotina de hidratação para pessoa com disfagia leve?"<br/>
                • "Como estimular cognição em fase inicial de demência?"<br/>
                A IA responde citando o manual e sugere <b>onde no plano incluir</b> essas indicações.
              </Callout>

              <Callout kind="warn" title="Nunca copiar 100% da IA">
                O output IA é <b>ponto de partida</b>, não versão final. Sempre reler, ajustar tom e
                remover generalidades. A IA não conhece a pessoa — tu conheces.
              </Callout>
            </Section>

            {/* 7 — Aprovações */}
            <Section id="aprovacoes" icon={ShieldCheck} kicker="Passo 7" title="Aprovações · TSC · DT · Gestor">
              <p>
                Antes de congelar uma versão, é obrigatório o trio <Kbd>tqa</Kbd> +
                <Kbd className="ml-1">dt</Kbd> + <Kbd className="ml-1">gestor</Kbd> aprovar
                (<Kbd>CarePlanApprovalsBlock</Kbd>). Cada aprovação regista quem, quando e opcionalmente notas.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--info))] font-semibold mb-1">TSC</div>
                  <h4 className="font-heading text-foreground mb-2">Técnica Qualidade & Acompanhamento</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Verifica coerência técnica do plano: objectivos SMART, riscos completos,
                    medicação sem doses, follow-up definido. Faz visita ao 15º dia para validar em campo.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--info))] font-semibold mb-1">DT</div>
                  <h4 className="font-heading text-foreground mb-2">Direcção Técnica</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Valida enquadramento legal (isenção IVA, tom institucional) e adequação clínica de
                    grande escala. Aprova aleatoriamente amostras — plano tem de estar bem.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--success))] font-semibold mb-1">Gestor</div>
                  <h4 className="font-heading text-foreground mb-2">Gestor de Cliente</h4>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Aprova a coerência com o combinado na RT + estimativa + contrato. É o dono
                    operacional da relação com a família.
                  </p>
                </div>
              </div>
              <Callout kind="info" title="Editar após aprovações">
                Qualquer edição a um bloco depois de todos aprovarem <b>reseta as 3 aprovações</b>
                (<Kbd>reason: "approval_reset"</Kbd>). Isso é intencional — mudanças materiais têm de
                voltar a passar pelo controlo. Se for só correcção ortográfica, adiciona nota no reset.
              </Callout>
            </Section>

            {/* 8 — Regras críticas */}
            <Section id="regras" icon={AlertTriangle} kicker="Passo 8" title="Regras críticas (Caring)">
              <div className="space-y-3">
                <div className="rounded-lg border border-border border-l-4 border-l-red-500 bg-[hsl(var(--danger-bg))]/30 p-4">
                  <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--danger))] font-semibold mb-2">Nunca escrever no PC</div>
                  <ul className="list-disc list-inside space-y-1 text-[13px] text-foreground">
                    <li>Doses de medicação, alterações de terapêutica, prescrições</li>
                    <li>Diagnósticos novos ou prognósticos ("vai piorar em 6 meses")</li>
                    <li>Promessas de resultados clínicos ("vai recuperar mobilidade")</li>
                    <li>Seguros, responsabilidade civil, acidentes de trabalho</li>
                    <li>Referências a IVA — os serviços Caring são <b>isentos</b>, nunca comunicar taxa</li>
                    <li>Linguagem emotiva ("com carinho", "quem tanto se doou")</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-border border-l-4 border-l-emerald-500 bg-[hsl(var(--success-bg))]/30 p-4">
                  <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--success))] font-semibold mb-2">Sempre incluir</div>
                  <ul className="list-disc list-inside space-y-1 text-[13px] text-foreground">
                    <li><b>Riscos + acção preventiva + resposta</b> para cada risco identificado</li>
                    <li><b>Contactos operacionais</b> — médico de família, farmácia, familiar-chave</li>
                    <li><b>Objectivos SMART</b> a 3 meses com indicador de sucesso</li>
                    <li><b>Follow-up plan</b> com datas concretas de reavaliação</li>
                    <li><b>Preferências</b> — o que a pessoa gosta / detesta</li>
                    <li>Se cliente tem <b>2 beneficiários</b>, distinguir explicitamente ("aplica-se apenas ao pai")</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* 9 — Checklist */}
            <Section id="checklist" icon={CheckCircle2} kicker="Passo 9" title="Checklist final — antes de pedir aprovações">
              <p className="text-muted-foreground mb-3">
                Passa esta lista antes de clicar <b>Pedir aprovações</b>. Se falha um item, volta
                atrás — vais poupar 2-3 dias em ida-e-volta com o TSC.
              </p>
              <ol className="space-y-2 list-none">
                {[
                  "Todos os 28 blocos foram tocados (mesmo os curtos)?",
                  "Cada risco tem uma acção preventiva + resposta se acontecer?",
                  "Contactos operacionais incluem médico + familiar + farmácia?",
                  "Objectivos são SMART com indicador de sucesso a 3 meses?",
                  "Vista Cliente foi lida com olhos de família — humana, sem jargão?",
                  "Vista AF tem tarefas diárias com horários concretos?",
                  "Nenhuma dose de medicação escrita (só nomes + timing)?",
                  "Nenhuma referência a IVA / seguros / responsabilidade civil?",
                  "Se há 2 beneficiários, cada tarefa/necessidade indica a qual se aplica?",
                  "Pré-visualização dos 3 .docx foi lida em modo leitor?",
                ].map((item, i) => (
                  <li key={i} data-testid={`cp-check-${i}`}
                      className="flex items-start gap-3 rounded-md border border-border bg-card p-3">
                    <div className="w-5 h-5 rounded border border-border shrink-0 mt-0.5" />
                    <div className="text-[13px] text-foreground flex-1">{item}</div>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex items-center gap-2 text-[13px] text-muted-foreground">
                <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
                Se todos os 10 itens estão OK → <b className="text-foreground">Pedir aprovações</b>.
                O sistema notifica TSC, DT e Gestor via ClickUp automaticamente.
              </div>
            </Section>

          </main>
        </div>
      </div>
    </div>
  );
}
